import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils"
import type {TypedImageData} from "@cm/utils"
import {ImageNode} from "#template/nodes/image/image-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ImageLikeNode, NumberLike} from "#template/nodes/types"
import {imageLike} from "#template/nodes/image/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import {generateImageNodeForInMemoryImageData} from "#template/nodes/image/in-memory-image"
import type {BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {materialConnection, UvMap, type ImageGenerator, type ImageNodeGeneratorData} from "@cm/material-nodes"
import type {BakedTextureData} from "#template/runtime-graph/nodes/bake-texture"
import {BakeTexture} from "#template/runtime-graph/nodes/bake-texture"
import {TypeDescriptors as TD} from "#template/runtime-graph/type-descriptors"
import type {CmLogger} from "@cm/log"

const bakedTextureDataDesc = TD.Identity<BakedTextureData>("contentHash")
import {visitNone} from "#template/template-node/visit-helpers"

function computeInitialSeedImageData(inputImage: TypedImageData, threshold: number) {
    const inputNumChannels = inputImage.channelLayout.length
    const inputData = (() => {
        if (!(inputImage.data instanceof Uint8Array || inputImage.data instanceof Float32Array))
            throw new Error("Input image expected to be Uint8Array or Float32Array")
        return inputImage.data
    })()
    // Int16 covers [-32768, 32767]: seed coords stay within [-max_dim, 2*max_dim), safe for images up to ~16k px
    const seedData = new Int16Array(inputImage.width * inputImage.height * 2)
    const seedMask = new Uint8Array(inputImage.width * inputImage.height)

    for (let y = 0; y < inputImage.height; y++) {
        for (let x = 0; x < inputImage.width; x++) {
            const value = inputData[(y * inputImage.width + x) * inputNumChannels] // NOTE: only values of the first channel are considered
            const isSeed = value < threshold
            seedData[(y * inputImage.width + x) * 2] = isSeed ? x : -1
            seedData[(y * inputImage.width + x) * 2 + 1] = isSeed ? y : -1
            seedMask[y * inputImage.width + x] = isSeed ? 1 : 0
        }
    }

    return {seedData, seedMask}
}

// seedMask - which pixels have a nearest-seed assigned; seedCoord - the assigned seed coordinate
function jumpFloodStep(seedMask: Uint8Array, seedCoord: Int16Array, width: number, height: number, stepSize: number): void {
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const maskIdx = y * width + x
            const coordIdx = maskIdx * 2
            const hasSeed = seedMask[maskIdx] !== 0
            let bestSeedX = hasSeed ? seedCoord[coordIdx] : 0
            let bestSeedY = hasSeed ? seedCoord[coordIdx + 1] : 0
            let bestDistSq = hasSeed ? (x - bestSeedX) * (x - bestSeedX) + (y - bestSeedY) * (y - bestSeedY) : Number.POSITIVE_INFINITY
            let updated = false

            for (let ox = -1; ox <= 1; ox++) {
                for (let oy = -1; oy <= 1; oy++) {
                    if (ox === 0 && oy === 0) continue

                    const neighborX = x + ox * stepSize
                    const neighborY = y + oy * stepSize
                    // Modulo wrap — works for any step size, no recursion, no object allocation
                    const nx = ((neighborX % width) + width) % width
                    const ny = ((neighborY % height) + height) % height
                    const neighborMaskIdx = ny * width + nx

                    if (seedMask[neighborMaskIdx] === 0) continue

                    const tileX = neighborX - nx
                    const tileY = neighborY - ny
                    const csx = seedCoord[neighborMaskIdx * 2] + tileX
                    const csy = seedCoord[neighborMaskIdx * 2 + 1] + tileY
                    const dx = x - csx
                    const dy = y - csy
                    const distSq = dx * dx + dy * dy

                    if (distSq < bestDistSq) {
                        bestDistSq = distSq
                        bestSeedX = csx
                        bestSeedY = csy
                        updated = true
                    }
                }
            }

            if (updated) {
                seedCoord[coordIdx] = bestSeedX
                seedCoord[coordIdx + 1] = bestSeedY
                seedMask[maskIdx] = 1
            }
        }
    }
}

export function computeImageDistanceTransformJumpFloodTileable(
    imageData: TypedImageData,
    threshold: number,
    refinementIterations: number,
    logger: CmLogger,
): TypedImageData {
    const {width, height} = imageData
    const numJfaSteps = Math.ceil(Math.log2(Math.max(width, height)))

    let t = performance.now()
    const {seedMask: seedMaskData, seedData: seedCoordData} = computeInitialSeedImageData(imageData, threshold)
    const tSeed = performance.now() - t

    t = performance.now()
    jumpFloodStep(seedMaskData, seedCoordData, width, height, 1)
    const tJfaPlus1 = performance.now() - t

    t = performance.now()
    for (let i = Math.max(width, height) / 2; i >= 1; i /= 2) jumpFloodStep(seedMaskData, seedCoordData, width, height, Math.floor(i))
    const tJfa = performance.now() - t

    t = performance.now()
    for (let i = refinementIterations; i > 0; i--) jumpFloodStep(seedMaskData, seedCoordData, width, height, i)
    const tRefinement = performance.now() - t

    t = performance.now()
    const distanceData = new Float32Array(width * height)
    let minDist = Number.POSITIVE_INFINITY
    let maxDist = Number.NEGATIVE_INFINITY

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = y * width + x
            const dx = x - seedCoordData[i * 2]
            const dy = y - seedCoordData[i * 2 + 1]
            const dist = seedMaskData[i] ? Math.sqrt(dx * dx + dy * dy) : Number.POSITIVE_INFINITY
            distanceData[i] = dist
            if (dist < minDist) minDist = dist
            if (dist > maxDist) maxDist = dist
        }
    }
    const tDist = performance.now() - t

    t = performance.now()
    for (let i = 0; i < distanceData.length; i++) distanceData[i] = (distanceData[i] - minDist) / (maxDist - minDist)
    const tNorm = performance.now() - t

    const tTotal = tSeed + tJfaPlus1 + tJfa + tRefinement + tDist + tNorm
    logger.debug(
        `ImageDistanceTransform [${width}×${height}, ${numJfaSteps} JFA steps, ${refinementIterations} refinements]: ` +
            `seed=${tSeed.toFixed(1)}ms, +1JFA=${tJfaPlus1.toFixed(1)}ms, JFA=${tJfa.toFixed(1)}ms, ` +
            `refinement=${tRefinement.toFixed(1)}ms, dist=${tDist.toFixed(1)}ms, norm=${tNorm.toFixed(1)}ms, ` +
            `total=${tTotal.toFixed(1)}ms`,
    )

    return {
        data: distanceData,
        width: imageData.width,
        height: imageData.height,
        channelLayout: "L",
        dataType: "float32",
        colorSpace: "linear",
    }
}

export type ImageDistanceTransformParameters = {
    name: string
    source: ImageLikeNode | null
    threshold: NumberLike
    resolution: NumberLike
    width: NumberLike
    height: NumberLike
    refinementIterations: NumberLike
}

const imageDistanceTransformDefinition: TemplateNodeDefinition<ImageDistanceTransformParameters> = {
    nodeClass: "ImageDistanceTransform",
    validation: {
        parameters: z.object({
            name: z.string(),
            source: z.nullable(imageLike),
            threshold: numberLike,
            resolution: numberLike,
            width: numberLike,
            height: numberLike,
            refinementIterations: numberLike,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ImageDistanceTransform extends ImageNode<ImageDistanceTransformParameters> {
    public override get definition() {
        return imageDistanceTransformDefinition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<ImageDistanceTransformParameters> {
        if (this.parameters.source === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<ImageGenerator | null> {
        const {templateContext} = evaluator
        const {sceneManager, logger} = templateContext

        const {source, threshold, resolution, width, height, refinementIterations} = this.parameters

        const [sourceValid, sourceInvalid] = scope.branch(evaluator.evaluate(source))
        const [thresholdValid, thresholdInvalid] = scope.branch(evaluator.evaluate(threshold))
        const [resolutionValid, resolutionInvalid] = scope.branch(evaluator.evaluate(resolution))
        const [widthValid, widthInvalid] = scope.branch(evaluator.evaluate(width))
        const [heightValid, heightInvalid] = scope.branch(evaluator.evaluate(height))
        const [refinementIterationsValid, refinementIterationsInvalid] = scope.branch(evaluator.evaluate(refinementIterations))

        const inputTextureNode = scope.pureLambda(
            sourceValid,
            (source) => ({
                node: source.imageNode.generator({
                    uv: materialConnection(
                        new UvMap({
                            parameters: {
                                inputs: {},
                                settings: {
                                    uvMapIndex: 0,
                                },
                            },
                        }),
                        "uv",
                    ),
                    extension: "REPEAT",
                    interpolation: "Closest",
                    projection: "FLAT",
                    disableProgressiveLoading: true,
                    forceOriginalResolution: true,
                }).color,
                contentHash: source.imageNode.hash,
            }),
            "bakeTextureInput",
        )

        const textureSizePx = scope.pureLambda(
            scope.tuple(widthValid, heightValid, resolutionValid),
            ([width, height, resolution]) => {
                return {width: Math.ceil(width * resolution), height: Math.ceil(height * resolution)}
            },
            "computeTextureSizePx",
        )

        const textureWidthPx = scope.get(textureSizePx, "width")
        const textureHeightPx = scope.get(textureSizePx, "height")

        const {bakedTextureData: bakedInput} = scope.node(BakeTexture, {
            sceneManager,
            textureNode: inputTextureNode,
            widthUV: widthValid,
            heightUV: heightValid,
            widthPx: textureWidthPx,
            heightPx: textureHeightPx,
        })

        // pure-lambda-allow: logger
        const imageDistanceTransform = scope.pureLambda(
            scope.tuple(scope.value(bakedInput, bakedTextureDataDesc, "bakedInput"), thresholdValid, refinementIterationsValid),
            ([bakedInput, threshold, refinementIterations]) => ({
                imageData: computeImageDistanceTransformJumpFloodTileable(bakedInput.imageData, threshold, refinementIterations, logger),
                contentHash: hashObject({
                    type: "ImageDistanceTransformData",
                    sourceHash: bakedInput.contentHash,
                    threshold,
                    refinementIterations,
                }),
            }),
            "computeImageDistanceTransform",
        )

        const result = scope.pureLambda(
            scope.tuple(sourceValid, imageDistanceTransform, resolutionValid, thresholdValid),
            ([source, imageDistanceTransform, resolution, threshold]) => {
                return {
                    imageNode: {
                        generator: (generatorData: ImageNodeGeneratorData) =>
                            generateImageNodeForInMemoryImageData(imageDistanceTransform.imageData, generatorData),
                        hash: hashObject({
                            type: "ImageDistanceTransform",
                            source: source.imageNode.hash,
                            distanceTransform: imageDistanceTransform.contentHash,
                            threshold,
                            resolution,
                        }),
                    },
                }
            },
            "imageDistanceTransformImageGenerator",
        )

        return scope.phi(result, sourceInvalid, thresholdInvalid, resolutionInvalid, widthInvalid, heightInvalid, refinementIterationsInvalid)
    }
}
